import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import {
  Storefront,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Eye,
  Trash,
  WifiHigh,
  WifiSlash,
  Drop,
  Cpu,
  Tag,
  CurrencyDollar,
  Lightning,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// API_URL imported from utils

const STORE_CATEGORIES = [
  { value: '1 Door', label: '1 Door' },
  { value: '2 Doors', label: '2 Doors' },
  { value: '3 Doors', label: '3 Doors' },
  { value: '4 Doors', label: '4 Doors' },
];

const INST_TYPES = [
  { value: 'University', label: 'University' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'School', label: 'School' },
  { value: 'Office', label: 'Office' },
  { value: 'Public', label: 'Public' },
];

const INPUT_WATER_TYPES = [
  { value: 'Fresh Water', label: 'Fresh Water' },
  { value: 'Tap Water', label: 'Tap Water' },
  { value: 'Borehole', label: 'Borehole' },
  { value: 'Rain Water', label: 'Rain Water' },
  { value: 'Salty Water', label: 'Salty Water' },
];

const initialFormData = {
  device_id: '',
  store_name: '',
  entity_id: '',
  campus_id: '',
  category: '',
  inst_type: '',
  input_water: '',
  mobile: '',
  permit_no: '',
  selling_price: '0',
  utility_water: '0',
  utility_electricity: '0',
  royalty: '0',
  operational_status: 'Operating',
  status: 'Active',
};

const OPERATIONAL_STATUS_OPTIONS = [
  { value: 'Operating', label: 'Operating' },
  { value: 'Fault', label: 'Fault' },
  { value: 'Under Maintenance', label: 'Under Maintenance' },
];

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

export default function StoresPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const [stores, setStores] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterCampus, setFilterCampus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [step, setStep] = useState(0);

  const storeSteps = [
    { label: 'Basic Info', icon: Storefront },
    { label: 'Assignment', icon: Tag },
    { label: 'Classification', icon: Cpu },
    { label: 'Pricing & Utilities', icon: CurrencyDollar },
    { label: 'Additional', icon: Drop },
  ];

  const fetchCampuses = useCallback(async (entityId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/campuses`;
      if (entityId && entityId !== 'all') url += `?entity_id=${entityId}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCampuses(data);
    } catch (error) {
      console.error('Error fetching campuses:', error);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/stores`;
      const params = [];
      if (filterEntity && filterEntity !== 'all') params.push(`entity_id=${filterEntity}`);
      if (filterCampus && filterCampus !== 'all') params.push(`campus_id=${filterCampus}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  }, [filterEntity, filterCampus]);

  const syncFromMiddleware = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const campusParam = filterCampus && filterCampus !== 'all' ? `?campus_id=${filterCampus}` : '';
      await axios.get(`${API_URL}/api/stores/sync-middleware${campusParam}`, { withCredentials: true, headers });
      await fetchStores();
    } catch (e) { /* silent */ }
  }, [filterCampus, fetchStores]);

  useEffect(() => { fetchStores(); syncFromMiddleware(); }, [fetchStores, syncFromMiddleware]);
  useEffect(() => { fetchCampuses(filterEntity); }, [filterEntity, fetchCampuses]);

  const resetForm = () => {
    setFormData({ ...initialFormData, entity_id: selectedEntity?.id || '' });
    setEditingStore(null);
    setStep(0);
  };

  const openCreateDialog = () => {
    resetForm();
    if (selectedEntity?.id) fetchCampuses(selectedEntity.id);
    setIsDialogOpen(true);
  };

  const openEditDialog = (store) => {
    setEditingStore(store);
    setStep(0);
    setFormData({
      device_id: store.device_id || '',
      store_name: store.store_name || '',
      entity_id: store.entity_id || '',
      campus_id: store.campus_id || '',
      category: store.category || '',
      inst_type: store.inst_type || '',
      input_water: store.input_water || '',
      mobile: store.mobile || '',
      permit_no: store.permit_no || '',
      selling_price: String(store.selling_price ?? 0),
      utility_water: String(store.utility_water ?? 0),
      utility_electricity: String(store.utility_electricity ?? 0),
      royalty: String(store.royalty ?? 0),
      operational_status: store.operational_status || 'Operating',
      status: store.status || 'Active',
    });
    fetchCampuses(store.entity_id);
    setIsDialogOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        ...formData,
        selling_price: parseFloat(formData.selling_price) || 0,
        utility_water: parseFloat(formData.utility_water) || 0,
        utility_electricity: parseFloat(formData.utility_electricity) || 0,
        royalty: parseFloat(formData.royalty) || 0,
      };
      // status/operational_status are only meaningful on update; strip on create
      if (!editingStore) {
        delete payload.status;
        delete payload.operational_status;
      }

      if (editingStore) {
        await axios.put(`${API_URL}/api/stores/${editingStore.id}`, payload, {
          withCredentials: true, headers
        });
        toast.success('Store updated successfully');
      } else {
        await axios.post(`${API_URL}/api/stores`, payload, {
          withCredentials: true, headers
        });
        toast.success('Store created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchStores();
    } catch (error) {
      const message = formatApiError(error, 'Failed to save store');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (store) => {
    if (!window.confirm(`Are you sure you want to delete ${store.store_name}?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL}/api/stores/${store.id}`, {
        withCredentials: true, headers
      });
      toast.success('Store deleted successfully');
      fetchStores();
    } catch (error) {
      toast.error('Failed to delete store');
    }
  };

  const toggleStatus = async (store) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const newStatus = store.operational_status === 'online' ? 'offline' : 'online';
      await axios.put(`${API_URL}/api/stores/${store.id}`, {
        operational_status: newStatus
      }, { withCredentials: true, headers });
      toast.success(`Store is now ${newStatus}`);
      fetchStores();
    } catch (error) {
      toast.error('Failed to update store status');
    }
  };

  const filteredStores = stores.filter(store =>
    store.store_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (store.store_code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (store.device_id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3 pb-6" data-testid="stores-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Stores
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={openCreateDialog}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="create-store-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Store
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name, code or device id…"
        testId="stores-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); setFilterCampus('all'); }}
        filters={[
          ...(isSuperAdmin ? [{
            key: 'entity',
            label: 'Entity',
            value: filterEntity,
            onChange: (v) => { setFilterEntity(v); setFilterCampus('all'); },
            options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
          }] : []),
          {
            key: 'campus',
            label: 'Campus',
            value: filterCampus,
            onChange: setFilterCampus,
            options: [{ value: 'all', label: 'All campuses' }, ...campuses.map((c) => ({ value: c.id, label: c.campus_name }))],
          },
        ]}
      />

      <DataTable
        columns={[
          { key: 'store', label: 'Store Name', width: 'minmax(160px,1fr)', render: (s) => {
            const isOperating = (s.operational_status || '').toLowerCase() === 'operating';
            return (
              <span className="flex items-center gap-3 min-w-0">
                <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center group-hover:bg-[#EDE6D8] ${isOperating ? 'bg-emerald-500/15' : 'bg-rose-500/15'}`}>
                  <Storefront size={16} className={`group-hover:text-[#1C1917] ${isOperating ? 'text-emerald-600' : 'text-rose-600'}`} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {s.store_name}
                  </span>
                </span>
              </span>
            );
          } },
          { key: 'campus_name', label: 'Campus', width: 'minmax(220px,2.4fr)', tone: 'muted', wrap: true },
          { key: 'inst_type', label: 'Institution', width: 'minmax(120px,1fr)', tone: 'muted' },
          { key: 'operational_status', label: 'Operational', width: 'minmax(130px,0.9fr)', render: (s) => {
            const op = s.operational_status || '—';
            const isOperating = String(op).toLowerCase() === 'operating';
            return (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full ${
                isOperating
                  ? 'bg-emerald-500/10 text-emerald-700 group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]'
                  : 'bg-rose-500/10 text-rose-700 group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]'
              }`}>
                {isOperating ? <WifiHigh size={12} /> : <WifiSlash size={12} />}
                {op}
              </span>
            );
          } },
          { key: 'status', label: 'Status', width: 'minmax(90px,0.7fr)', render: (s) => {
            const isActive = String(s.status || '').toLowerCase() === 'active';
            return (
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
                <span className={`text-xs font-medium ${isActive ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {s.status || '—'}
                </span>
              </span>
            );
          } },
        ]}
        rows={filteredStores}
        loading={loading}
        rowKey={(s) => s.id}
        onRowClick={(s) => navigate(`/stores/${s.id}`)}
        emptyIcon={<Storefront size={20} className="text-slate-500" />}
        emptyTitle="No stores yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Add a store or sync from middleware.'}
        testIdPrefix="store"
        actionRender={(s) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); navigate(`/stores/${s.id}`); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-store-${s.id}`}
              aria-label="View store"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      {/* Create/Edit Dialog — Multi-Step */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) { setStep(0); } setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editingStore ? 'Edit Store' : 'Create Store'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingStore ? 'Edit store details' : 'Create a new store'}
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-between mb-6 px-2">
            {storeSteps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <button
                    type="button"
                    onClick={() => setStep(i)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isActive
                        ? 'bg-[#0A2540] text-white'
                        : isDone
                          ? 'bg-[#10B981]/10 text-[#10B981]'
                          : 'bg-[#FCFAF6] text-[#78716C]'
                    }`}
                    data-testid={`store-step-${i}`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </button>
                  {i < storeSteps.length - 1 && (
                    <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[#10B981]/30' : 'bg-[#EDE6D8]'}`} />
                  )}
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 0: Basic Information */}
            {step === 0 && (
              <div className="space-y-4" data-testid="store-step-basic">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Enter the store name and device identifier.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Store Name <span className="text-[#EF4444]">*</span></Label>
                    <Input
                      value={formData.store_name}
                      onChange={(e) => updateField('store_name', e.target.value)}
                      placeholder="e.g., Water Station 1"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Device ID <span className="text-[#EF4444]">*</span></Label>
                    <Input
                      value={formData.device_id}
                      onChange={(e) => updateField('device_id', e.target.value)}
                      placeholder="e.g., DEV-001"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-device-id-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Assignment */}
            {step === 1 && (
              <div className="space-y-4" data-testid="store-step-assignment">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Assign this store to an entity and campus.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {isSuperAdmin && (
                    <div className="space-y-2">
                      <Label>Entity <span className="text-[#EF4444]">*</span></Label>
                      <Select
                        value={formData.entity_id}
                        onValueChange={(value) => {
                          updateField('entity_id', value);
                          updateField('campus_id', '');
                          fetchCampuses(value);
                        }}
                      >
                        <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-entity-select">
                          <SelectValue placeholder="Select entity" />
                        </SelectTrigger>
                        <SelectContent>
                          {entities.map((entity) => (
                            <SelectItem key={entity.id} value={entity.id}>{entity.entity_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Campus <span className="text-[#EF4444]">*</span></Label>
                    <Select
                      value={formData.campus_id}
                      onValueChange={(value) => updateField('campus_id', value)}
                    >
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-campus-select">
                        <SelectValue placeholder="Select campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {campuses.map((campus) => (
                          <SelectItem key={campus.id} value={campus.id}>{campus.campus_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Classification */}
            {step === 2 && (
              <div className="space-y-4" data-testid="store-step-classification">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Select the store category, institution type, and water source.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Category <span className="text-[#EF4444]">*</span></Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => updateField('category', value)}
                    >
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {STORE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Institution Type <span className="text-[#EF4444]">*</span></Label>
                    <Select
                      value={formData.inst_type}
                      onValueChange={(value) => updateField('inst_type', value)}
                    >
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-inst-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INST_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Input Water <span className="text-[#EF4444]">*</span></Label>
                    <Select
                      value={formData.input_water}
                      onValueChange={(value) => updateField('input_water', value)}
                    >
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-input-water-select">
                        <SelectValue placeholder="Select water type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INPUT_WATER_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Pricing & Utilities */}
            {step === 3 && (
              <div className="space-y-4" data-testid="store-step-pricing">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Set the unit selling price, utility costs, and royalty for this store.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <CurrencyDollar size={12} className="text-[#0A2540]" />
                      Selling Price
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.selling_price}
                      onChange={(e) => updateField('selling_price', e.target.value)}
                      placeholder="e.g., 175"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-selling-price-input"
                    />
                    <p className="text-[11px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Price per unit dispensed.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Royalty (%)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.royalty}
                      onChange={(e) => updateField('royalty', e.target.value)}
                      placeholder="e.g., 10"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-royalty-input"
                    />
                    <p className="text-[11px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Percentage of sales taken as royalty.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Drop size={12} className="text-[#0A2540]" />
                      Utility Water
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.utility_water}
                      onChange={(e) => updateField('utility_water', e.target.value)}
                      placeholder="e.g., 1223.21"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-utility-water-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Lightning size={12} className="text-[#0A2540]" />
                      Utility Electricity
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.utility_electricity}
                      onChange={(e) => updateField('utility_electricity', e.target.value)}
                      placeholder="e.g., 210.11"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-utility-electricity-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Additional Info */}
            {step === 4 && (
              <div className="space-y-4" data-testid="store-step-additional">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Provide optional contact and permit details.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mobile Number</Label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) => updateField('mobile', e.target.value)}
                      placeholder="e.g., 255700000000"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-mobile-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Permit Number</Label>
                    <Input
                      value={formData.permit_no}
                      onChange={(e) => updateField('permit_no', e.target.value)}
                      placeholder="Permit number (optional)"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="store-permit-no-input"
                    />
                  </div>
                </div>

                {editingStore && (
                  <div className="pt-4 mt-4 border-t border-[#EDE6D8] space-y-3">
                    <p className="text-xs font-semibold text-[#1C1917] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Status & Operations
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => updateField('status', value)}
                        >
                          <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Operational Status</Label>
                        <Select
                          value={formData.operational_status}
                          onValueChange={(value) => updateField('operational_status', value)}
                        >
                          <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="store-operational-status-select">
                            <SelectValue placeholder="Select operational status" />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERATIONAL_STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Footer */}
            <DialogFooter className="pt-6 mt-6 border-t border-[#EDE6D8] flex justify-between">
              <div>
                {step > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="store-step-back-btn"
                  >
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-[#EDE6D8] rounded-lg"
                >
                  Cancel
                </Button>
                {step < storeSteps.length - 1 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (step === 0 && (!formData.store_name || !formData.device_id)) {
                        toast.error('Store Name and Device ID are required');
                        return;
                      }
                      setStep(step + 1);
                    }}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                    data-testid="store-step-next-btn"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                    data-testid="save-store-btn"
                  >
                    {submitting ? 'Saving...' : (editingStore ? 'Update' : 'Create')}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
